/* ============================================================================
   COOPER HOME PAGE — BUILD YOUR OWN SET, AND ACTUALLY BUY IT

   REPLACES assets/cooper-bundle.js, which is now unreferenced. That file did
   three things — remember which of three cards were tapped, keep the panel
   honest about them, hold the button shut — and its state model was the reason
   it could never add to a cart: the CHOSEN THING WAS THE CARD. A card is a
   category ("bucket hat"), and a category has no variant id, so there was
   nothing to post.

   THE STATE IS NOW ON THE SLOTS, not on the cards. Each <li data-bundle-slot>
   in the panel carries the whole of one piece in its dataset — variant id, the
   product's title, the size, the price as the shop itself formatted it, the
   buyer's words, the thread — and the cards are only doors into a picker. Three
   consequences, all of them wanted:

     1. A LINE CAN BE POSTED. Every field /cart/add.js needs is on the slot.
     2. THE SAME CATEGORY CAN BE TAKEN TWICE. Two different bucket hats is a
        real order, and it is the only way a three-piece set can be completed at
        all today: the neck scarf collection is empty, so there are two live
        categories and three slots. Under the old one-card-one-choice model the
        button would have read "Pick 1 more" for ever.
     3. NOTHING IS REMEMBERED OUTSIDE THE DOM, which was the old file's rule and
        is kept. Deriving the count, the total and the button's state from the
        slots on every repaint is why removing a piece needs no special case.

   THE ADD IS A BUTTON AND A fetch, NOT A FORM SUBMIT, and that is trap J
   answered by construction rather than by patch. Grepped before writing a line:
   assets/cooper-cart.js:1019 listens for `submit` ON THE DOCUMENT and posts the
   FormData of any form[action*="/cart/add"] it sees, and it is loaded on the
   home page (sections/cooper-cart.liquid is in popup-group.json, which every
   page renders). assets/cart.js:1145 listens for the shipping calculator.
   assets/cooper-pdp.js:1030 listens on the product form itself and has to call
   stopPropagation to stay out of the cart's way — that is the fix that cost a
   day. A control that is not in a form and never fires `submit` cannot be
   intercepted by any of them, and there is nothing here for a native POST to
   fall back to anyway: no picker, no JavaScript, no chosen variant.

   THE POST SHAPE IS COPIED FROM assets/cooper-pdp.js, not reinvented: one
   {items: [...]} array, one entry per piece with its own id, quantity 1 and its
   own properties, and ONE embroidery line for the whole order however many
   pieces carry words. Then /cart, because settings_data has cart_type "page".
   ========================================================================= */
(() => {
  const money = () => window.CooperMoney || null;

  /* WHOLE CENTS. 93.70 - 77.90 is 15.799999999999997 in a double and the pack
     check is an exact match against a figure the owner typed to two decimals. */
  const cents = (v) => Math.round(v * 100);

  /* THE SHAPE OF A FORMATTED PRICE with the digits replaced by a marker:
     "€34,90" -> "€#", "34,90 €" -> "# €". Two prices of different shape are two
     currencies or two conventions, and striking one out beside the other would
     be an invented exchange rate. Same function as the old file's. */
  const shape = (parsed) => parsed.template.replace(parsed.token, '#').trim();

  const readJSON = (node) => {
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      /* A broken data island is a bug in the section, not something to hide
         from whoever is looking for it. */
      console.warn('[bundle] product data would not parse', e);
      return null;
    }
  };

  function bind(box) {
    box.dataset.bundleSetBound = '';

    const slots = [...box.querySelectorAll('[data-bundle-slot]')];
    if (!slots.length) return;

    const slotList = box.querySelector('[data-bundle-slots]');
    const adds = [...box.querySelectorAll('[data-bundle-add]')];
    const countNode = box.querySelector('[data-bundle-count]');
    const totalNode = box.querySelector('[data-bundle-total]');
    const cta = box.querySelector('[data-bundle-cta]');
    const ctaLabel = box.querySelector('[data-bundle-cta-label]');
    const buy = box.querySelector('[data-bundle-buy]');
    const addonNode = box.querySelector('[data-bundle-addon]');
    const errorNode = box.querySelector('[data-bundle-error]');
    /* PRESENT ONLY WHEN THE CAP CAN REALLY STARVE THE SET — the section renders
       it when (bound categories x cap) is smaller than the number of slots, and
       leaves it out entirely otherwise. That gate is why this can be a second
       polite live region on a panel whose own comment warns against having two:
       in the configuration that renders it, the buy button can never be pressed
       (the set can never be completed), so the failure line above it can never
       be speaking at the same time. */
    const stuckNode = box.querySelector('[data-bundle-stuck]');

    /* ALL FOUR ARE ABSENT UNLESS THE SAVING GATE IS TICKED. Read as a group: no
       pack list means no pack can match, so the other three are never written
       to even if some future markup leaves them behind. */
    const wasNode = box.querySelector('[data-bundle-was]');
    const pillNode = box.querySelector('[data-bundle-saving]');
    const sayNode = box.querySelector('[data-bundle-say]');
    /* ABSENT UNLESS A DISCOUNT REALLY EXISTS — the section does not render it
       otherwise, so every read below is guarded and the middle-state line simply
       never appears. */
    const nudgeNode = box.querySelector('[data-bundle-nudge]');
    const packs = [...box.querySelectorAll('[data-bundle-pack]')].map((n) => ({
      items: Number.parseInt(n.dataset.packItems, 10),
      /* THE CATEGORIES THIS PACK MEANS, sorted once here so the comparison
         below is a string equality rather than a set operation on every
         repaint. Empty means "match on the count alone", which is what every
         pack written before tonight does. */
      cols: (n.dataset.packCollections || '')
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean)
        .sort()
        .join('|'),
      price: n.dataset.packPrice || '',
      saving: n.dataset.packSaving || '',
    }));

    /* THE PICKER. Every one of these is optional: with no collection bound to
       any shelf item the section renders as the shop window it used to be, with
       a link for a button, and the whole block below reduces to nothing. */
    const region = box.querySelector('[data-bundle-pick]');
    const detail = box.querySelector('[data-bundle-detail]');
    const groups = [...box.querySelectorAll('[data-bundle-pick-group]')];
    const stepNode = box.querySelector('[data-bundle-pick-step]');
    /* The summary card, so the last confirm can bring it back into view. The
       modal covers it while it is open, which is the point of the modal and
       also the reason the buyer needs showing where it went. */
    const panel = box.querySelector('.cooper-bundle__panel');

    const want = slots.length;
    const filled = () => slots.filter((s) => s.dataset.variant);
    const firstEmpty = () => slots.find((s) => !s.dataset.variant) || null;

    /* ----------------------------------------------------------------------
       HOW MANY PIECES ONE CATEGORY MAY GIVE, AND WHY THIS FILE'S OWN HEADER
       ARGUES THE OTHER WAY.

       The header above says, in point 2: "THE SAME CATEGORY CAN BE TAKEN TWICE.
       Two different bucket hats is a real order, and it is the only way a
       three-piece set can be completed at all today: the neck scarf collection
       is empty, so there are two live categories and three slots. Under the old
       one-card-one-choice model the button would have read 'Pick 1 more' for
       ever."

       HALF OF THAT STILL HOLDS AND HALF OF IT HAS BEEN OVERRULED. Two different
       bucket hats is still a real order and is still allowed; a set of three is
       not, because the owner has said so — "потом я могу в бандл положить сразу
       3 кепки тоже бред или 3 панамы". What survives untouched is the second
       sentence, the arithmetic: with two live categories and three slots the set
       must be allowed to take a category twice or it can never be finished, and
       an unfillable buy button is the fault this shop has already been told off
       for. That is exactly why the cap is TWO and not one, and why it is a
       setting rather than a constant — see the schema note beside
       `per_category_max` for the counting, and for what to change it to on the
       day the neck scarves are photographed and there are three live categories
       instead of two.

       READ OFF THE SECTION ROOT, AND MISSING MEANS OFF. The asset and the
       section are pushed separately and a browser caches them separately, so
       this script will certainly run once against section markup that predates
       the attribute. `want` as the fallback is not a guess: a cap equal to the
       number of slots cannot refuse anything a full set has not already refused,
       so old markup keeps exactly today's behaviour instead of locking a shelf.
       -------------------------------------------------------------------- */
    const capRaw = Number.parseInt(box.dataset.bundleCap, 10);
    const cap = Number.isFinite(capRaw) && capRaw > 0 ? capRaw : want;

    /* IS THERE ROOM FOR ONE MORE PIECE FROM THIS CATEGORY, counted off the
       slots and nothing else, every time it is asked.

       THE SLOT BEING FILLED IS EXCLUDED FROM ITS OWN COUNT, and that single
       line is what keeps a typo fixable. Changing the words on the second of two
       bucket hats reopens the hat picker while the hats are already at their cap
       of two; counting the slot that is about to be overwritten would refuse the
       buyer his own piece back. Excluded, the sum is "the OTHER slots holding
       this category", so a swap is always allowed and only an ADDITION can be
       refused — which is the whole of what the owner asked for.

       AN UNKNOWN CATEGORY IS ALLOWED. A filled slot with no `group` cannot be
       attributed, and refusing on ignorance would shut a shelf over a missing
       attribute; the count simply does not see it, in the same direction as the
       missing-attribute case above. */
    const roomFor = (groupId, slot) => {
      if (!groupId) return true;
      let mine = 0;
      for (const s of filled()) {
        if (s === slot) continue;
        if ((s.dataset.group || '') === groupId) mine += 1;
      }
      return mine < cap;
    };

    /* WHAT THE PICKER IS CURRENTLY FILLING. One slot and one product, and they
       are cleared together — a target without a product is how a confirm lands
       in the wrong slot. */
    let target = null;
    let opener = null;
    let product = null;

    const say = (text) => {
      if (!errorNode) return;
      errorNode.textContent = text || '';
      errorNode.hidden = !text;
    };

    /* ----------------------------------------------------------------------
       THE SHELF CARDS MIRROR THE SET, and this reverses a decision made an hour
       earlier in the opposite direction.

       The argument for a stable shelf was that it is a menu of three categories,
       and that a card showing one particular garment would lie about its
       category as soon as a second piece of that category joined the set. The
       owner overruled it — "замена фото идет там где слева главное
       меню при выборе" — and he is right about what he was looking at: he
       chose a brown hat and the card went on advertising the blue one. And the
       objection dies with the cap he set in the same message: at ONE piece per
       category a card can never stand for two garments at once.

       RESTORING IS THE HARD HALF. The photograph is rendered by Liquid with a
       src, a srcset and an alt, and A srcset THAT IS STILL THERE WINS — setting
       src alone changes nothing on screen. So all three are stashed on the
       element itself the first time it is touched, and put back exactly when the
       piece leaves the set. On the element and not in a closure variable,
       because the theme editor re-renders this section and re-runs the binder,
       and a variable would then be holding a stale "original".

       THE SIZE. The island's url is 320px wide because the picker draws a 160px
       card with it; the shelf frame is 316px, so 320 is right for one device
       pixel and soft on a retina screen. Shopify's own width parameter is
       rewritten to 640 where the url carries one, falling back to the url
       untouched — no extra bytes in the island, and nothing invented if the CDN
       url shape ever changes.
       -------------------------------------------------------------------- */
    const cards = [...box.querySelectorAll('[data-bundle-item]')];

    const bigger = (url) => {
      if (!url) return url;
      /* Only the exact parameter Liquid produced, and only when it is the 320 we
         know we asked for. A blind regex on any width= would also rewrite a url
         somebody had deliberately pinned. */
      return url.indexOf('width=320') > -1 ? url.replace('width=320', 'width=640') : url;
    };

    const paintCards = () => {
      /* One pass over the filled slots first, so the card loop is a lookup
         rather than a search inside a search. */
      const chosen = new Map();
      for (const s of filled()) {
        const g = s.dataset.group || '';
        if (g && !chosen.has(g)) chosen.set(g, s);
      }

      for (const card of cards) {
        const door = card.querySelector('[data-bundle-add]');
        const img = card.querySelector('.cooper-bundle__frame img');
        if (!door || !img) continue;
        const slot = chosen.get(door.dataset.group || '') || null;

        if (!img.dataset.baseSrc) {
          img.dataset.baseSrc = img.getAttribute('src') || '';
          img.dataset.baseSrcset = img.getAttribute('srcset') || '';
          img.dataset.baseAlt = img.getAttribute('alt') || '';
        }

        if (slot && slot.dataset.shot) {
          const want = bigger(slot.dataset.shot);
          if (img.getAttribute('src') !== want) {
            /* The srcset goes FIRST. Left in place it would keep winning and the
               swap would be invisible — which is exactly how this looked wrong
               the first time it was tried. */
            img.removeAttribute('srcset');
            img.setAttribute('src', want);
          }
          if (slot.dataset.title) img.setAttribute('alt', slot.dataset.title);
          card.dataset.chosen = 'true';
        } else if (card.dataset.chosen) {
          img.setAttribute('src', img.dataset.baseSrc || '');
          if (img.dataset.baseSrcset) {
            img.setAttribute('srcset', img.dataset.baseSrcset);
          } else {
            img.removeAttribute('srcset');
          }
          img.setAttribute('alt', img.dataset.baseAlt || '');
          delete card.dataset.chosen;
        }
      }
    };

    /* ----------------------------------------------------------------------
       THE PANEL. Everything drawn here is derived from the slots, every time.
       -------------------------------------------------------------------- */
    const paint = () => {
      const picked = filled();
      paintCards();

      slots.forEach((slot) => {
        const nameNode = slot.querySelector('[data-bundle-slot-name]');
        const wordsNode = slot.querySelector('[data-bundle-slot-words]');
        const open = slot.querySelector('[data-bundle-slot-open]');
        const drop = slot.querySelector('[data-bundle-slot-drop]');
        const n = slot.dataset.bundleSlot;
        const has = Boolean(slot.dataset.variant);

        if (has) {
          slot.dataset.filled = 'true';
          const size = slot.dataset.size ? ', ' + slot.dataset.size : '';
          if (nameNode) nameNode.textContent = (slot.dataset.title || '') + size;
          /* THE WORDS ARE QUOTED so a piece that says nothing and a piece that
             says the word "nothing" cannot read the same. */
          if (wordsNode) {
            wordsNode.textContent = slot.dataset.words
              ? '\u201c' + slot.dataset.words + '\u201d'
              : (slotList && slotList.dataset.plainLabel) || '';
          }
        } else {
          delete slot.dataset.filled;
          if (nameNode) nameNode.textContent = '';
          if (wordsNode) wordsNode.textContent = '';
        }

        if (open) {
          open.hidden = !has;
          if (has) {
            open.setAttribute(
              'aria-label',
              ((slotList && slotList.dataset.changeLabel) || 'Change {n}').replace('{n}', n),
            );
          }
        }
        if (drop) {
          drop.hidden = !has;
          drop.setAttribute(
            'aria-label',
            ((slotList && slotList.dataset.dropLabel) || 'Remove {n}').replace('{n}', n),
          );
        }
      });

      if (countNode) countNode.textContent = String(picked.length);

      /* WHAT EACH CATEGORY HAS ALREADY GIVEN, DERIVED HERE AND REMEMBERED
         NOWHERE. The slots are the only record of the set, so the tally is built
         from them on every repaint rather than kept in a counter beside them —
         which is the same reason removing a piece needs no special case, and the
         reason a cap cannot drift out of step with what is actually in the
         panel. `group` is the picker's own element id, which is one per shelf
         block, so it IS the category. */
      const taken = new Map();
      for (const s of picked) {
        const g = s.dataset.group || '';
        if (g) taken.set(g, (taken.get(g) || 0) + 1);
      }

      /* A FULL SET STOPS TAKING MORE. The doors stay focusable and keep saying
         what they are — aria-disabled, not disabled — because the way out of a
         full set is the panel, and a control that vanishes teaches nothing.

         AND A CATEGORY THAT HAS GIVEN ITS CAP STOPS TOO, in the same breath and
         by the same means, but it says something different: the control carries
         the limit in words. A greyed button with no sentence is the thing this
         section already refused to do for the empty neck scarf card — see the
         `aria-disabled` note beside that control in the section — and this state
         needs the explanation more, not less, because unlike an empty collection
         it appeared as a consequence of something the buyer just did.

         WHICH REASON THE LABEL GIVES MATTERS. A full set refuses every category,
         and blaming the cap for that would be a small lie, so the cap wording
         only appears while there is still a slot to fill. `room` is totted up in
         the same pass because the button below has to know whether ANY category
         can still give a piece. */
      const full = picked.length >= want;
      let room = 0;
      for (const b of adds) {
        const spare = Math.max(0, cap - (taken.get(b.dataset.group || '') || 0));
        room += spare;
        const capped = spare === 0;
        b.setAttribute('aria-disabled', full || capped ? 'true' : 'false');
        const face = b.querySelector('[data-bundle-add-label]');
        if (face) {
          /* An empty string is never written: section markup older than these
             two attributes would otherwise have its buttons wiped blank. */
          const next = capped && !full ? b.dataset.capLabel || '' : b.dataset.addLabel || '';
          if (next && face.textContent !== next) face.textContent = next;
        }
      }

      /* --------------------------------------------------------------------
         THE MONEY. The honest sum of the garments comes first and always: it is
         what the pack price is checked against, what gets struck through, and
         what the panel falls back to the moment anything does not add up.

         THE PRICES ARE THE SHOP'S OWN STRINGS, put on each slot by the picker
         from `variant.price | money`. Nothing here formats a number from cents:
         the shop's money format is a setting, and cooper-money.js exists so an
         amount can be printed back in the shape it arrived in.
         ----------------------------------------------------------------- */
      const m = money();
      let honestText = null;
      let honestSum = null;
      let honestLike = null;
      let allPriced = false;

      /* THE STITCHING CHARGE IS ONE LINE FOR THE WHOLE ORDER, and the panel has
         to say so before the cart does. It appears the moment any piece carries
         words and it is IN the Total, because a total that is not the payable
         figure is the same lie as a saving nobody receives. */
      const written = picked.filter((s) => (s.dataset.words || '').trim().length > 0);
      /* `addonLive` still decides whether the charge goes into the Total and
         whether a pack price may be claimed. What it no longer does is reveal
         anything: the element is a hidden data carrier now, not a row. See the
         comment beside it in sections/cooper-bundle.liquid. */
      const addonLive = written.length > 0 && Boolean(addonNode);

      if (totalNode) {
        const amounts = picked
          .map((s) => (m ? m.parse(s.dataset.price) : null))
          .filter(Boolean);

        if (!m || !amounts.length) {
          totalNode.textContent = '\u2014';
        } else {
          honestSum = amounts.reduce((a, x) => a + x.value, 0);
          honestLike = amounts[0];
          honestText = m.print(honestSum, honestLike);
          /* EVERY CHOSEN PIECE MUST HAVE PARSED, not just one. A total a little
             short beats a dash, but a SAVING measured against a short sum is a
             wrong number, so the pack step below refuses to run without this. */
          allPriced = amounts.length === picked.length;

          let payable = honestSum;
          if (addonLive) {
            const addon = m.parse(addonNode.dataset.addonPrice);
            if (addon) payable += addon.value;
          }
          totalNode.textContent = m.print(payable, honestLike);
        }
      }

      /* --------------------------------------------------------------------
         THE PACK. Unchanged in its arithmetic from the file this replaces, with
         ONE new gate: a set that carries words is not the set the owner priced.

         His pack figure is a price for garments. Adding the stitching charge on
         top makes the printed set price stop being the payable figure, and the
         struck sum stop being the thing it is struck against. Rather than
         invent which of the two his 77,90 was meant to include, the pack simply
         does not apply once anything is written on, and the panel shows the
         honest payable total instead. Same rule as everywhere else here: a
         figure that might be wrong is not printed.

         AND IT IS ALL DORMANT TODAY. The gate that renders wasNode, pillNode,
         sayNode and the pack list at all is off — measured, not assumed: a
         three-piece set posted to a real cart on 25 August 2026 came back
         total_discount 0 and cart_level_discount_applications []. See the
         section's comment.
         ----------------------------------------------------------------- */
      /* WHICH CATEGORIES ARE IN THE SET, as the same sorted string the packs
         carry. Read off the group each slot came through, because that is where
         the collection handle is; a slot whose group has gone (an editor
         re-render, say) simply contributes nothing, in the direction of
         claiming less rather than more. */
      const setCols = [
        ...new Set(
          picked
            .map((s) => {
              const g = s.dataset.group ? document.getElementById(s.dataset.group) : null;
              return g ? (g.dataset.collection || '').trim().toLowerCase() : '';
            })
            .filter(Boolean),
        ),
      ]
        .sort()
        .join('|');

      /* THE STITCHING NO LONGER WITHDRAWS THE CLAIM UNLESS IT COSTS SOMETHING.
         It used to, unconditionally, because the charge went into the total
         while the pack price did not include it. The stitching is free today, so
         the guard is made conditional rather than deleted: put a price back on
         the add-on product and the old careful behaviour returns by itself. */
      const addonCosts = addonLive && m && (() => {
        const a = addonNode && m.parse(addonNode.dataset.addonPrice);
        return !!a && a.value > 0;
      })();

      let claim = null;
      const pack =
        packs.length && honestSum !== null && allPriced && !addonCosts
          ? packs.find(
              (p) =>
                p.items === picked.length && (!p.cols || p.cols === setCols),
            )
          : null;

      if (pack && m && pack.price) {
        const packPrice = m.parse(pack.price);
        if (packPrice && shape(packPrice) === shape(honestLike)) {
          const gap = cents(honestSum) - cents(packPrice.value);
          if (gap > 0) {
            if (!pack.saving) {
              claim = {price: pack.price, saving: ''};
            } else {
              const saving = m.parse(pack.saving);
              /* His figure, to the cent, or nothing. Never `gap` printed as if
                 it were his figure: a subtracted saving drifts with the buyer's
                 taste, and only one of those numbers is the one the checkout
                 would honour. */
              if (saving && cents(saving.value) === gap) {
                claim = {price: pack.price, saving: pack.saving};
              }
            }
          }
        }
      }

      if (wasNode && pillNode && totalNode) {
        if (claim) {
          totalNode.textContent = claim.price;
          wasNode.textContent = honestText;
          wasNode.hidden = false;
          if (claim.saving) {
            const label = pillNode.dataset.label || '';
            pillNode.textContent = (label + ' ' + claim.saving).trim();
          }
          pillNode.dataset.shown = claim.saving ? 'true' : 'false';
        } else {
          wasNode.hidden = true;
          wasNode.textContent = '';
          pillNode.dataset.shown = 'false';
        }
      }

      if (sayNode) {
        let line = '';
        const totalLabel = sayNode.dataset.totalLabel || '';
        const wasLabel = sayNode.dataset.wasLabel || '';
        if (claim) {
          line = totalLabel + ' ' + claim.price + '. ' + wasLabel + ' ' + honestText + '.';
          if (claim.saving) {
            line += ' ' + ((pillNode && pillNode.dataset.label) || '') + ' ' + claim.saving + '.';
          }
        } else if (sayNode.textContent && totalNode) {
          line = totalLabel + ' ' + totalNode.textContent + '.';
        }
        /* Writing the same string again re-announces it in several screen
           readers, and paint() runs on every tap. */
        if (sayNode.textContent !== line) sayNode.textContent = line;
      }

      if (cta && ctaLabel) {
        /* THE MINIMUM, NOT THE SLOT COUNT. The button used to open only on a
           full set; the owner has opened it at two — "можно купить и 2 пункта в
           бандле на главной". `want` is still what the set IS, and it is still
           what the discount is for; `min` is only what the basket will accept.
           Read off the DOM with the slot count as the fallback, so an old
           section rendered against a new script goes back to all-or-nothing
           rather than to a button that takes one piece. */
        const minRaw = Number.parseInt(cta.dataset.min || '', 10);
        const min = Number.isFinite(minRaw) && minRaw > 0 ? Math.min(minRaw, want) : want;
        const left = min - picked.length;
        const ready = left <= 0;
        const whole = picked.length >= want;

        /* THE DEAD END, WHICH IS THE ONLY NEW WAY THIS BUTTON CAN LIE.
           "Pick 1 more" is a lie the moment there is nothing left to pick with:
           every category at its cap, a slot still empty, and every door on the
           shelf already refused. The button then says so and stays shut, and the
           note under it says the way out — take a piece out. An honest dead end
           is allowed; a button that looks ready and does nothing is not.

           `adds.length > 0` IS LOAD-BEARING, NOT A TIDY GUARD. With no shelf
           item bound to a stocked collection there are no doors at all, `room`
           is zero for want of anywhere to count, and this section is the shop
           window it began as: a link that says "Take these to the buy box". That
           branch must keep its own label, so the dead end is only claimed where
           there was really something to add and it has really run out. The same
           condition is what puts a buy button on the page instead of the link.

           AND IT CANNOT HAPPEN AT ALL IN THE SHIPPED CONFIGURATION. room is
           (bound categories x cap) - picked, so a dead end needs
           picked = categories x cap < want: two categories at a cap of two is
           four against three slots, so the state is unreachable today and the
           note is not even rendered. It exists for a cap of one, which the owner
           may legitimately set the day the scarves land, and for whatever he
           types next year. */
        const stuck = !ready && room === 0 && adds.length > 0;

        cta.setAttribute('aria-disabled', ready ? 'false' : 'true');
        /* THREE LABELS, AND THE MIDDLE ONE IS NEW. A buyable two-piece order must
           not read "Put the set in the basket" — it is not the set, and the word
           would quietly claim the discount that only the whole set gets. */
        ctaLabel.textContent = ready
          ? whole
            ? cta.dataset.readyLabel || ''
            : (cta.dataset.partialLabel || cta.dataset.readyLabel || '').replace(
                '{n}',
                String(picked.length),
              )
          : stuck && cta.dataset.stuckLabel
            ? cta.dataset.stuckLabel
            : (cta.dataset.waitingLabel || '').replace('{n}', String(left));

        /* WHAT THE LAST PIECE IS WORTH, said only where it is both true and
           useful: a discount really exists, the basket would already take this
           order, and the set is not finished. The element is not even rendered
           unless the owner has ticked that a discount exists, so this is a
           second lock on the same door. */
        if (nudgeNode) {
          /* WHAT THE NEXT PIECE ADDS, not what the whole set saves.

             The old line printed the set's own figure -- "the whole set is 13,80
             less" -- which was true of the set and wrong as an incentive the
             moment two pieces started showing a saving of their own. Measured on
             the live panel: two pieces already save 6,90, so 13,80 read as what
             was still on the table when 6,90 was. The step, not the total.

             `current` is the pack THIS selection already qualifies for, computed
             once above; this block used to shadow it with a const of the same
             name, which is the only reason the outer one looks new here. When the
             selection qualifies for nothing yet the step is the whole figure,
             which is also correct -- two caps and no bandana really do gain all
             13,80 by adding one. */
          const target = packs.find((p) => p.items === want);
          const current = pack;
          let step = '';
          if (target && target.saving && m) {
            const whole_ = m.parse(target.saving);
            const now = current && current.saving ? m.parse(current.saving) : null;
            if (whole_) {
              /* DIFFERENCED IN CENTS, PRINTED IN UNITS, and the first version of
                 this got the second half wrong -- it handed cents to m.print and
                 the sentence read "save 690,00 more". cents() here multiplies by
                 100; m.print takes what m.parse produced.

                 The subtraction stays in integers on purpose: 13.80 - 6.90 in
                 floats is 6.900000000000001 often enough to matter on a figure
                 the buyer is invited to check against the checkout. Dividing
                 integers back by 100 is exact. */
              const gainCents = cents(whole_.value) - (now ? cents(now.value) : 0);
              if (gainCents > 0) step = m.print(gainCents / 100, whole_);
            }
          }
          const show = ready && !whole && !!step;
          if (show) {
            const line = (nudgeNode.dataset.template || '').replace('{saving}', step);
            if (nudgeNode.textContent !== line) nudgeNode.textContent = line;
          }
          nudgeNode.hidden = !show;
        }

        /* The sentence itself is printed by the section, not written from here:
           it never changes, and in View Source it explains a hidden element that
           would otherwise look like a bug. */
        if (stuckNode) stuckNode.hidden = !stuck;
      }
    };

    /* ----------------------------------------------------------------------
       THE PICKER. An in-flow panel, NOT a <dialog>, and the reason is on file:
       this shop has already found that a Shopify form inside a modal dialog
       cannot reach the visible CAPTCHA fallback (400, "Missing CAPTCHA token"),
       and the shape it settled on is to read the choices out of the picker and
       post them from outside it. There is no form in here at all, so that
       boundary cannot be crossed by accident; and an in-flow panel needs no
       focus trap, no inert and no top layer, none of which this section would
       have had a second surface to test.

       IT IS NOT GIVEN THE .reveal CLASS. Our sections reveal on scroll and the
       reveal replays, so an element that is revealed by script while the
       viewport is somewhere else is an element that paints blank.
       -------------------------------------------------------------------- */
    /* ----------------------------------------------------------------------
       THE PICKER IS A MODAL DIALOG NOW, AND THAT IS THE OWNER'S BUG ANSWERED
       BY CONSTRUCTION RATHER THAN BY DECORATION.

       What he wrote, 25 August 2026: "бандлы на главной не удобно что оно идет
       все вниз теряется ключевая кнопка добавить / надо сделать поп ап в
       котором можно выбрать и удобно закрыть". The panel used to open IN FLOW
       between the shelf and the summary card, so on a phone choosing a hat
       pushed the summary card — and with it the ONLY button that can put the
       set in a basket — a whole screen further down.

       The in-flow panel was the right first shape for one stated reason: no
       focus trap to write, no inert, no top layer to test. A native <dialog>
       gives all three for free, and takes the picker out of the flow entirely
       so that nothing below it can move. The section's tokens still reach it:
       custom properties inherit down the DOM tree, and the top layer changes
       where an element PAINTS, not where it sits in the tree.

       THE SCROLL LOCK IS OURS, NOT THE BROWSER'S. A modal dialog blocks clicks
       behind its backdrop but the page still scrolls under it in Chrome. This
       is deliberately the SAME mechanism the cart drawer uses at
       assets/cooper-cart.js:261 — an inline overflow on the root element —
       rather than a second one, because two ways of locking the same page is
       how one of them gets left on.
       -------------------------------------------------------------------- */
    const rootEl = document.documentElement;
    const lock = () => {
      /* THE GUTTER IS RESERVED BEFORE THE SCROLLBAR IS TAKEN AWAY, and this is
         not a nicety — it is a measured 14px. Hiding the root's overflow removes
         the 15px scrollbar, the page gets 15px wider, the shelf's two-column
         grid reflows into it and the summary card jumps 14px up the moment the
         modal opens. Measured on the live home page at 2532px: -14 without this
         line, 0 with it.

         GUARDED, BECAUSE THE GUTTER CUTS BOTH WAYS. On a page short enough to
         have no scrollbar, or in a browser with overlay scrollbars, reserving
         one ADDS 15px that was never there and shifts the layout the other way.
         The width is therefore measured first and the gutter only asked for
         when there is really a bar to replace. */
      const bar = window.innerWidth - rootEl.clientWidth;
      if (bar > 0) rootEl.style.scrollbarGutter = 'stable';
      rootEl.style.overflow = 'hidden';
    };
    const unlock = () => {
      rootEl.style.overflow = '';
      rootEl.style.scrollbarGutter = '';
    };

    /* Whether closing hands focus back to the control that opened the picker.
       Escape and a backdrop click arrive through the dialog's own `close` event
       with no argument to carry, so the wish is parked here first. It defaults
       to true because that is the right answer for Escape. */
    let refocusOnClose = true;

    const resetPicker = () => {
      for (const g of groups) g.hidden = true;
      if (detail) detail.hidden = true;
      if (region) region.dataset.step = 'list';
      for (const b of adds) b.setAttribute('aria-expanded', 'false');
      target = null;
      product = null;
      unlock();
      if (refocusOnClose && opener && document.contains(opener)) opener.focus();
      opener = null;
      refocusOnClose = true;
    };

    const closePicker = (refocus) => {
      refocusOnClose = refocus !== false;
      if (region && region.open) {
        /* close() fires `close`, and resetPicker runs from there — so Escape,
           the backdrop, the cross and this call all take exactly one path. */
        if (typeof region.close === 'function') {
          region.close();
          return;
        }
        region.removeAttribute('open');
      }
      resetPicker();
    };

    if (region) region.addEventListener('close', resetPicker);

    /* ----------------------------------------------------------------------
       THE FACE ON THE CLOSED PICKER has to show the colour that is checked, or
       the control says nothing at all while shut — which is the whole reason it
       can be 58px wide instead of a row of five captioned circles.
       -------------------------------------------------------------------- */
    const threadBox = detail ? detail.querySelector('[data-bundle-thread]') : null;
    const paintThreadFace = () => {
      if (!threadBox) return;
      const face = threadBox.querySelector('[data-bundle-picker-face]');
      const dot = threadBox.querySelector('[data-bundle-thread-dot]:checked');
      if (face && dot) face.style.backgroundColor = dot.dataset.hex || '';
    };

    if (threadBox) {
      threadBox.addEventListener('change', () => {
        paintThreadFace();
        /* SHUT ON CHOICE. A picker that stays open after a colour is picked
           covers the confirm button underneath it, which on a phone is the next
           thing the thumb reaches for. */
        threadBox.open = false;
      });
      paintThreadFace();
    }

    /* ----------------------------------------------------------------------
       THE SIZE GUIDE FOR WHICHEVER CATEGORY IS OPEN. The url is on the group,
       so this only has to read it — and hide the control when the category has
       no chart, so it is never a button that does nothing.

       THE IMAGE IS POINTED AT ITS URL ONLY WHEN THE PANEL IS FIRST OPENED, not
       when the detail step is shown: a 1000px chart fetched for every garment a
       buyer merely looks at is a megabyte nobody asked for, and `loading="lazy"`
       cannot help inside a closed details element in every browser.
       -------------------------------------------------------------------- */
    const chartBox = detail ? detail.querySelector('[data-bundle-chart]') : null;
    const chartImg = chartBox ? chartBox.querySelector('[data-bundle-chart-img]') : null;

    const setChart = (group) => {
      if (!chartBox) return;
      const url = (group && group.dataset.sizeChart) || '';
      const alt = (group && group.dataset.sizeChartAlt) || '';
      chartBox.open = false;
      chartBox.hidden = !url;
      chartBox.dataset.url = url;
      chartBox.dataset.alt = alt;
      if (chartImg && chartImg.getAttribute('src') && chartImg.getAttribute('src') !== url) {
        chartImg.removeAttribute('src');
      }
    };

    if (chartBox && chartImg) {
      chartBox.addEventListener('toggle', () => {
        if (!chartBox.open) return;
        const url = chartBox.dataset.url || '';
        if (url && chartImg.getAttribute('src') !== url) {
          chartImg.setAttribute('src', url);
          chartImg.setAttribute('alt', chartBox.dataset.alt || '');
        }
      });
    }

    const sizeName = 'cooper-bundle-size-' + (box.id || 'set');

    const showDetail = (p) => {
      if (!detail) return;
      product = p;

      const img = detail.querySelector('[data-bundle-detail-img]');
      const title = detail.querySelector('[data-bundle-detail-title]');
      const price = detail.querySelector('[data-bundle-detail-price]');
      const sizes = detail.querySelector('[data-bundle-sizes]');
      const words = detail.querySelector('[data-bundle-words]');
      const thread = detail.querySelector('[data-bundle-thread]');
      const confirm = detail.querySelector('[data-bundle-confirm]');

      if (img) {
        if (p.img) {
          img.src = p.img;
          img.alt = p.alt || '';
          img.hidden = false;
        } else {
          img.hidden = true;
        }
      }
      if (title) title.textContent = p.t || '';
      if (price) price.textContent = p.p || '';

      /* THE SIZES ARE REAL RADIOS, created here rather than written into the
         page. Native single-select, native arrow keys, and no aria-checked to
         keep in step with a class. They group by `name` whether or not they are
         in a form, and there is no form. */
      if (sizes) {
        sizes.textContent = '';
        for (const v of p.v || []) {
          const label = document.createElement('label');
          label.className = 'cooper-bundle__size';
          const input = document.createElement('input');
          input.type = 'radio';
          input.name = sizeName;
          input.value = String(v.id);
          input.dataset.size = v.o || '';
          input.dataset.price = v.p || p.p || '';
          if (!v.a) {
            input.disabled = true;
            label.dataset.gone = 'true';
          }
          const face = document.createElement('span');
          face.textContent = v.o || '';
          label.appendChild(input);
          label.appendChild(face);
          input.addEventListener('change', () => {
            if (confirm) confirm.disabled = false;
          });
          sizes.appendChild(label);
        }
      }

      /* PREFILLED WHEN THE BUYER CAME BACK TO CHANGE THIS PIECE, and only when
         it is the same garment: carrying a hat's size across to a cap would be
         a guess printed into a size field. */
      const same = target && target.dataset.handle === (p.h || '');
      if (words) words.value = same ? target.dataset.words || '' : '';
      /* THE PICKER IS RADIOS NOW, NOT A SELECT, so coming back to a piece means
         checking the right dot rather than assigning a value. If the remembered
         colour is no longer in the list the dots are left as they are, which
         leaves the default checked rather than nothing. */
      if (thread && same && target.dataset.thread) {
        const back = [...thread.querySelectorAll('[data-bundle-thread-dot]')].find(
          (i) => i.value === target.dataset.thread,
        );
        if (back) back.checked = true;
      }
      paintThreadFace();
      if (confirm) confirm.disabled = true;
      if (same && sizes) {
        const back = [...sizes.querySelectorAll('input')].find(
          (i) => i.value === target.dataset.variant,
        );
        if (back && !back.disabled) {
          back.checked = true;
          if (confirm) confirm.disabled = false;
        }
      }

      detail.hidden = false;
      /* ONE QUESTION AT A TIME INSIDE THE MODAL. The list stays in the DOM and
         keeps its scroll position — the stylesheet hides it on this step — so
         "Back to the list" returns to the same place in 21 hats rather than to
         the top of them. */
      if (region) region.dataset.step = 'detail';
      /* Same reasoning as the list step: the dialog takes focus and borrows the
         name of whatever is now the heading of it, which on this step is the
         garment being configured. */
      const head = detail.querySelector('[data-bundle-detail-focus]');
      if (region) {
        if (head) {
          if (!head.id) head.id = (region.id || 'cooper-bundle') + '-detail-title';
          region.setAttribute('aria-labelledby', head.id);
        }
        region.focus({preventScroll: true});
      }
    };

    const renderList = (group) => {
      const list = group.querySelector('[data-bundle-pick-list]');
      if (!list || list.dataset.built) return;
      const data = readJSON(group.querySelector('[data-bundle-pick-data]')) || [];

      /* BUILT ON FIRST OPEN, THEN KEPT. 35 garments of markup and 35 <img> in
         the home page's HTML is weight every visitor pays for a panel most
         never open, and this page has already had 1077 KB taken out of it once.
         The JSON island is the same trade the buy box makes at
         [data-pdp-variants]: the DOM does not know a variant's id, its price or
         whether it is in stock, and guessing any of the three is how a shop
         sells something it does not have. */
      for (const p of data) {
        const li = document.createElement('li');
        const card = document.createElement('button');
        card.type = 'button';
        card.className = 'cooper-bundle__pick-card';
        card.dataset.handle = p.h || '';

        if (p.img) {
          const img = document.createElement('img');
          img.src = p.img;
          img.alt = p.alt || '';
          img.width = 160;
          img.height = 160;
          img.loading = 'lazy';
          img.decoding = 'async';
          card.appendChild(img);
        }
        /* ТОЛЬКО РАЗЛИЧАЮЩАЯ ЧАСТЬ ЗАГОЛОВКА -- та же причина и то же правило,
           что в модалке карточки товара: немецкие заголовки начинаются родовой
           головой «Hundehalstuch mit Namen | », которая в карточке занимает
           четыре строки и ничего не сообщает. Заголовок без черты не трогается.
           Полный остаётся в title и в доступном имени кнопки. */
        const shortName = (full) => {
          const parts = String(full || '')
            .split('|')
            .map((s) => s.trim())
            .filter(Boolean);
          if (parts.length < 2) return String(full || '');
          return parts.slice(1).join(' | ');
        };

        const t = document.createElement('span');
        t.className = 'cooper-bundle__pick-name';
        t.textContent = shortName(p.t);
        t.title = p.t || '';
        card.appendChild(t);

        const pr = document.createElement('span');
        pr.className = 'cooper-bundle__pick-price tech';
        pr.textContent = p.p || '';
        card.appendChild(pr);

        /* SOLD OUT IS SAID, NOT HIDDEN. A garment whose every size is gone is
           still the one in the photograph the buyer came back for. */
        const anyLeft = (p.v || []).some((v) => v.a);
        if (!anyLeft) {
          card.setAttribute('aria-disabled', 'true');
          const s = document.createElement('span');
          s.className = 'cooper-bundle__pick-gone tech';
          s.textContent = group.dataset.goneLabel || '';
          card.appendChild(s);
        }

        card.setAttribute(
          'aria-label',
          [p.t || '', p.p || '', anyLeft ? '' : group.dataset.goneLabel || '']
            .filter(Boolean)
            .join(', ')
        );

        card.addEventListener('click', () => {
          if (card.getAttribute('aria-disabled') === 'true') return;
          showDetail(p);
        });

        li.appendChild(card);
        list.appendChild(li);
      }
      list.dataset.built = 'true';
    };

    const openGroup = (group, slot, button) => {
      if (!region || !group) return;
      say('');
      target = slot;
      opener = button || null;
      for (const g of groups) g.hidden = g !== group;
      if (detail) detail.hidden = true;
      region.dataset.step = 'list';
      setChart(group);
      renderList(group);
      for (const b of adds) {
        b.setAttribute('aria-expanded', b.dataset.group === group.id ? 'true' : 'false');
      }

      /* WHICH PIECE IS BEING FILLED, SAID INSIDE THE DIALOG. A modal covers the
         panel it is filling, so the count that used to be a glance away is now
         behind the backdrop — and without this line the second and third trips
         through the picker are indistinguishable from the first. */
      if (stepNode) {
        const n = slots.indexOf(slot) + 1;
        stepNode.textContent =
          n > 0
            ? (region.dataset.stepLabel || '')
                .replace('{n}', String(n))
                .replace('{total}', String(want))
            : '';
      }

      if (!region.open) {
        if (typeof region.showModal === 'function') {
          region.showModal();
        } else {
          /* NO TOP LAYER IN THIS BROWSER. The dialog then opens in flow exactly
             where the old panel did — a worse experience and a working one —
             which is why the section still renders it inside the layout grid
             rather than at the end of the document, and why it still gets the
             scroll-into-view the modal path has no use for. */
          region.setAttribute('open', '');
          region.scrollIntoView({block: 'nearest', behavior: 'smooth'});
        }
      }
      lock();

      /* FOCUS LANDS ON THE DIALOG, NOT ON THE HEADING, and the heading's own id
         is borrowed for the dialog's name. Measured on the live page: focusing
         the heading by script made it match :focus-visible even after a mouse
         click, so every opening drew a ring round the title. A modal dialog is
         the thing that has just appeared, so it is the correct place for focus
         to be — and with aria-labelledby pointed at the heading it announces
         "Choose your bucket hat" rather than the section's own name. */
      const head = group.querySelector('[data-bundle-pick-title]');
      if (head && head.id) region.setAttribute('aria-labelledby', head.id);
      region.focus({preventScroll: true});
    };

    const groupById = (id) => groups.find((g) => g.id === id) || null;

    for (const button of adds) {
      button.addEventListener('click', () => {
        const group = groupById(button.dataset.group || '');
        if (!group) return;

        /* A DOOR THAT HAS ALREADY GIVEN ITS PIECE LEADS BACK TO IT.

           The owner: "если я хочу поменять панаму я не могу это сделать потому
           что модал уже заблокирован." Changing a piece was in fact already
           possible from the slot row on the right, which reopens the picker at
           the garment already chosen -- but he pressed the CARD, which is the
           obvious thing to press, and the card refused him. A second way in that
           nobody finds is not a way in.

           So a capped door reopens its own piece, by the same two calls the slot
           row makes, so the two paths cannot drift apart. THE CAP IS NOT
           WEAKENED: this leads to the piece that is already in the set, and
           confirming replaces it in the slot it already occupies. Adding a
           second garment of the same category is still refused below, on the
           empty-slot path, which is the one that could break the arithmetic.

           IT ALSO UNLOCKS A FULL SET. Three of three used to refuse every door,
           leaving a buyer who simply wanted a different hat with no move except
           emptying a slot first. */
        const mine = slots.find(
          (sl) => sl.dataset.group === group.id && sl.dataset.variant,
        );
        if (mine) {
          const openBtn = mine.querySelector('[data-bundle-slot-open]');
          openGroup(group, mine, openBtn || button);
          const data = readJSON(group.querySelector('[data-bundle-pick-data]')) || [];
          const again = data.find((p) => p.h === mine.dataset.handle);
          if (again) showDetail(again);
          return;
        }

        if (button.getAttribute('aria-disabled') === 'true') return;
        const slot = firstEmpty();
        if (!slot) return;
        /* THE REFUSAL IS THE ARITHMETIC, NOT THE ATTRIBUTE. paint() greys this
           control and relabels it when the category is used up, but
           aria-disabled is a statement about a button and this is the statement
           about the set: asked again here, off the slots, at the moment of the
           press. It is what makes the cap true rather than merely drawn. */
        if (!roomFor(group.id, slot)) return;
        /* The slot remembers which door it came through, so "change" can reopen
           the same collection without asking the buyer again. */
        slot.dataset.group = group.id;
        openGroup(group, slot, button);
      });
    }

    for (const slot of slots) {
      const open = slot.querySelector('[data-bundle-slot-open]');
      const drop = slot.querySelector('[data-bundle-slot-drop]');
      if (open) {
        open.addEventListener('click', () => {
          const group = groupById(slot.dataset.group || '');
          if (!group) return;
          openGroup(group, slot, open);
          /* Straight back to the garment that is already in the slot: the
             commonest reason to return here is a typo in the words, not a change
             of mind about the hat. */
          const data = readJSON(group.querySelector('[data-bundle-pick-data]')) || [];
          const again = data.find((p) => p.h === slot.dataset.handle);
          if (again) showDetail(again);
        });
      }
      if (drop) {
        drop.addEventListener('click', () => {
          /* `group` JOINS THE LIST, AND IT MATTERS NOW THAT IT DID NOT BEFORE.
             It used to be left behind harmlessly — the only thing that read it
             was the change button, which is hidden on an empty slot. It is now
             the key the per-category tally is counted on, and leaving a category
             written on a slot that holds nothing is the kind of thing a later
             reader counts by accident. Emptied means belonging to nobody. */
          for (const k of ['variant', 'title', 'size', 'price', 'words', 'thread', 'handle', 'group', 'shot']) {
            delete slot.dataset[k];
          }
          if (target === slot) closePicker(false);
          say('');
          paint();
        });
      }
    }

    if (detail) {
      const confirm = detail.querySelector('[data-bundle-confirm]');
      const back = detail.querySelector('[data-bundle-back]');
      const words = detail.querySelector('[data-bundle-words]');

      const commit = () => {
        if (!target || !product) return;
        const chosen = detail.querySelector('[data-bundle-sizes] input:checked');
        if (!chosen) return;

        /* THE CATEGORY IS WRITTEN WITH THE PIECE, not left as whatever was put
           on the slot when the picker opened. Exactly one group is unhidden
           while a detail is open — openGroup hides the rest, and the "Back to
           the list" handler below already reads the open one this way — so this
           is the category the buyer is actually standing in. Writing it here
           means the tally's key arrives in the same breath as the variant it
           belongs to, and a slot can never hold a piece the count cannot
           attribute.

           THE CHECK IS AN ASSERTION AND SHOULD NEVER FIRE. Both doors into a
           picker are already narrower than this: the Add button asks roomFor
           before it opens, and the change button opens the slot's own category,
           which roomFor allows by excluding that slot from its own count. It is
           here because this is where a piece becomes part of the set, and
           because the non-modal fallback leaves the panel clickable behind an
           open picker — the one place where the state could move under it. It
           says so in the console rather than silently, since a confirm button
           that does nothing is a bug and not a state. */
        const source = groups.find((g) => !g.hidden) || null;
        const groupId = (source && source.id) || target.dataset.group || '';
        if (!roomFor(groupId, target)) {
          console.warn('[bundle] refused a piece: that category is at its cap', groupId);
          return;
        }
        if (groupId) target.dataset.group = groupId;
        const thread = detail.querySelector('[data-bundle-thread]');
        const w = words ? words.value.trim() : '';
        target.dataset.variant = chosen.value;
        target.dataset.size = chosen.dataset.size || '';
        target.dataset.price = chosen.dataset.price || '';
        target.dataset.title = product.t || '';
        target.dataset.handle = product.h || '';
        /* THE FACE OF THE PIECE, COPIED OFF THE ISLAND AT THE MOMENT THE PIECE
           IS MADE, beside the variant and the title and for the same reason:
           everything the panel draws comes off the slot, so a repaint never has
           to go looking for the product again. Stored even when the product has
           no photograph, as the empty string, so paint can tell "no picture"
           from "not asked yet" without reaching for the island. */
        target.dataset.shot = product.img || '';
        target.dataset.words = w;
        /* THE THREAD IS A PROPERTY OF THE WORDS. A blank piece carries no
           colour, so an unwritten garment cannot reach the workshop with a
           thread choice nobody made. */
        /* THE CHECKED DOT, and still only when there are words: a blank piece
           carries no colour, so an unwritten garment cannot reach the workshop
           with a thread choice nobody made. */
        const dot = thread && thread.querySelector('[data-bundle-thread-dot]:checked');
        target.dataset.thread = w && dot ? dot.value : '';

        /* THE LAST PIECE ENDS SOMEWHERE DIFFERENT, and this is the other half
           of "теряется ключевая кнопка добавить". A modal stops the basket
           button being PUSHED away; this stops it being merely off screen once
           the set is complete. Focus goes to the button rather than back to the
           shelf card, and preventScroll keeps the browser's own jump to the
           focused element from cancelling the smooth scroll above it. */
        const complete = !firstEmpty();
        closePicker(!complete);
        paint();
        if (complete) {
          if (panel) panel.scrollIntoView({block: 'nearest', behavior: 'smooth'});
          if (buy && buy.getAttribute('aria-disabled') !== 'true') {
            buy.focus({preventScroll: true});
          }
        }
      };

      if (confirm) confirm.addEventListener('click', commit);
      if (words) {
        words.addEventListener('keydown', (event) => {
          /* There is no form, so Enter does nothing on its own — and it is the
             key the hand reaches for after typing the words. */
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          }
        });
      }
      if (back) {
        back.addEventListener('click', () => {
          detail.hidden = true;
          if (region) region.dataset.step = 'list';
          product = null;
          const group = groups.find((g) => !g.hidden);
          const head = group && group.querySelector('[data-bundle-pick-title]');
          if (region) {
            if (head && head.id) region.setAttribute('aria-labelledby', head.id);
            region.focus({preventScroll: true});
          }
        });
      }
    }

    for (const close of box.querySelectorAll('[data-bundle-pick-close]')) {
      close.addEventListener('click', () => closePicker(true));
    }
    if (region) {
      /* ESCAPE IS THE BROWSER'S once the dialog is modal: it fires `cancel` and
         then `close`, and resetPicker runs off the second. The handler below is
         only for the fallback path, where the element is an ordinary box with
         an `open` attribute and no key does anything on its own. */
      if (typeof region.showModal !== 'function') {
        region.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') closePicker(true);
        });
      }

      /* A PRESS ON THE BACKDROP CLOSES IT, and the test is geometric rather
         than by target. The backdrop and the dialog's own padding are the SAME
         element as far as an event target is concerned, so closing on
         `event.target === region` alone would also close the modal when
         somebody pressed the gap beside the heading. */
      region.addEventListener('click', (event) => {
        if (event.target !== region) return;
        const r = region.getBoundingClientRect();
        const inside =
          event.clientX >= r.left &&
          event.clientX <= r.right &&
          event.clientY >= r.top &&
          event.clientY <= r.bottom;
        if (!inside) closePicker(true);
      });
    }

    /* ----------------------------------------------------------------------
       THE ADD. One POST, one line per piece, one embroidery line for the order.
       -------------------------------------------------------------------- */
    if (buy) {
      buy.addEventListener('click', async () => {
        if (buy.getAttribute('aria-disabled') === 'true') return;
        const picked = filled();
        /* THE SAME MINIMUM THE LABEL USES. Read again here rather than passed
           down, because this handler runs on a click that may be a long time
           after the last repaint, and because a button that says it will buy
           two must actually buy two. */
        const minRaw = Number.parseInt(buy.dataset.min || '', 10);
        const minBuy = Number.isFinite(minRaw) && minRaw > 0 ? Math.min(minRaw, want) : want;
        if (picked.length < minBuy) return;

        const lineProp = buy.dataset.lineProperty || 'Your line';
        const threadProp = buy.dataset.threadProperty || 'Thread';

        const items = [];
        const threads = [];
        for (const slot of picked) {
          const id = Number.parseInt(slot.dataset.variant, 10);
          if (!Number.isFinite(id) || id <= 0) {
            say(buy.dataset.errorLabel || '');
            return;
          }
          const w = (slot.dataset.words || '').trim();
          const properties = {};
          if (w) {
            properties[lineProp] = w;
            /* Leaving the thread off an embroidered line is how an order once
               reached the workshop with words and no colour. */
            if (slot.dataset.thread) {
              properties[threadProp] = slot.dataset.thread;
              if (!threads.includes(slot.dataset.thread)) threads.push(slot.dataset.thread);
            }
          }
          items.push({id, quantity: 1, properties});
        }

        /* ONE EMBROIDERY LINE FOR THE WHOLE ORDER, quantity one, if ANYTHING is
           written. The owner's rule and the one cooper-pdp.js follows: it is one
           setup, not one per garment. The charge line names every thread in the
           order so the person at the machine sees the whole job on the line that
           pays for it. */
        const addonId = addonNode
          ? Number.parseInt(addonNode.dataset.addonVariant, 10)
          : NaN;
        const anyWords = picked.some((s) => (s.dataset.words || '').trim().length > 0);
        if (anyWords && Number.isFinite(addonId) && addonId > 0) {
          items.push({
            id: addonId,
            quantity: 1,
            properties: threads.length ? {[threadProp]: threads.join(', ')} : {},
          });
        }

        buy.setAttribute('aria-disabled', 'true');
        buy.setAttribute('aria-busy', 'true');
        const held = ctaLabel ? ctaLabel.textContent : '';
        if (ctaLabel && buy.dataset.busyLabel) ctaLabel.textContent = buy.dataset.busyLabel;
        say('');

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {'Content-Type': 'application/json', Accept: 'application/json'},
            body: JSON.stringify({items}),
          });
          const data = await res.json().catch(() => null);
          if (!res.ok) {
            throw new Error(
              (data && (data.description || data.message)) || 'cart/add.js ' + res.status,
            );
          }
          /* THE DRAWER, NOT A PAGE LOAD -- the same correction the product page
             got, in the file that was missed. The owner: "бандл на главной если
             я нажму добавить в корзину меня перенаправляет на страницу а не на
             модал".

             assets/cooper-cart.js listens for this on the document and refetches
             the cart as it opens, so this file needs to know nothing about how
             the drawer is built.

             THE PAGE LOAD STAYS AS THE FALLBACK: on a template with no drawer
             mounted, dispatching into nothing would leave the buyer looking at
             the set he just bought wondering where it went.

             THE BUTTON IS HANDED BACK HERE, and it has to be. The restore below
             lives in `catch`, so it only ever ran on failure -- which was fine
             while this ended in a page load, because the page left before anyone
             could look at the button. Measured after the change: aria-busy stayed
             true and the label stayed busy, so closing the drawer to build a
             second set found a dead control. Not restored on the navigation path,
             where the page is leaving anyway. */
          if (document.querySelector('[data-cooper-cart]')) {
            document.dispatchEvent(new Event('cooper:cart:open'));
            buy.removeAttribute('aria-busy');
            if (ctaLabel) ctaLabel.textContent = held;
            paint();
          } else {
            window.location.assign('/cart');
          }
        } catch (e) {
          /* NEVER LEAVE THE BUTTON DEAD. There is no form to fall back to, so
             the failure is said in words and the control handed straight back. */
          console.warn('[bundle] add failed', e);
          say(String((e && e.message) || e));
          buy.removeAttribute('aria-busy');
          if (ctaLabel) ctaLabel.textContent = held;
          paint();
        }
      });
    }

    if (cta && !buy) {
      cta.addEventListener('click', (event) => {
        /* aria-disabled is a promise to a screen reader; this is what makes it
           true for everyone else. A link cannot be disabled in HTML. */
        if (cta.getAttribute('aria-disabled') === 'true') event.preventDefault();
      });
    }

    paint();
  }

  function wire(root) {
    const scope = root instanceof Element ? root : document;
    for (const box of scope.querySelectorAll('.cooper-bundle:not([data-bundle-set-bound])')) {
      bind(box);
    }
  }

  wire(document);
  document.addEventListener('shopify:section:load', (event) => wire(event.target));
})();
